/**
 * BackupSeedPhrase — Show seed phrase after password verification.
 *
 * Security: requires password re-entry, shows words in grid, copy button.
 */

import { useState, useCallback, type ReactNode } from 'react';
import { getWalletService } from '../walletService.js';
import { decrypt } from '../crypto.js';
import { loadVault } from '../storage.js';

export function BackupSeedPhrase({ onBack }: { onBack: () => void }): ReactNode {
  const [password, setPassword] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleReveal = useCallback(async () => {
    if (!password) {
      setError('Enter your password to reveal the seed phrase');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const stored = await loadVault();
      if (!stored) {
        setError('No wallet found');
        return;
      }

      const phrase = await decrypt(
        stored.vault.ciphertext,
        password,
        stored.vault.iv,
        stored.vault.salt
      );

      setMnemonic(phrase);
      setRevealed(true);
    } catch {
      setError('Wrong password');
    } finally {
      setLoading(false);
    }
  }, [password]);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(mnemonic);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [mnemonic]);

  if (!revealed) {
    return (
      <div className="screen">
        <div className="screen-header">
          <button className="btn-ghost" onClick={onBack}>Back</button>
          <h2>Backup Seed Phrase</h2>
        </div>
        <div className="screen-body">
          <p className="text-secondary warning">
            Your seed phrase is the master key to your wallet. Anyone with these words can access your funds. Never share them.
          </p>

          <div className="form-group">
            <label>Enter your password to reveal</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter wallet password"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && void handleReveal()}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            className="btn-primary btn-full"
            onClick={() => void handleReveal()}
            disabled={loading || !password}
          >
            {loading ? 'Decrypting...' : 'Reveal Seed Phrase'}
          </button>
        </div>
      </div>
    );
  }

  const words = mnemonic.split(' ');

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onBack}>Done</button>
        <h2>Your Seed Phrase</h2>
      </div>
      <div className="screen-body">
        <p className="text-secondary warning">
          Write these words down and store them safely offline. Do not screenshot or save digitally.
        </p>

        <div className="mnemonic-grid">
          {words.map((word, i) => (
            <div key={i} className="mnemonic-word">
              <span className="mnemonic-index">{i + 1}</span>
              <span className="mnemonic-text">{word}</span>
            </div>
          ))}
        </div>

        <button className="btn-secondary btn-full" onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy to Clipboard'}
        </button>

        <button className="btn-ghost btn-full" onClick={onBack}>
          I have backed it up
        </button>
      </div>
    </div>
  );
}
